import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import Groups2Icon from "@mui/icons-material/Groups2";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import TuneIcon from "@mui/icons-material/Tune";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import api from "../services/api";
import { FRONTEND_URL } from "../config/urls";
import { getPathHreflangLinks } from "../seo/hreflang";
import { buildRecipePath } from "../utils/recipeUrl";
import { resolveRecipeImage } from "../utils/assets";
import { useLocationCtx } from "../context/LocationContext";
import { resolveCountryId } from "../utils/country";
import { useAuth } from "../context/AuthContext";
import { userHasActiveEntitlement } from "../utils/entitlements";

const DEFAULT_PEOPLE = 4;
const DEFAULT_BUDGET = 20;
const GUEST_FALLBACK_LIMIT = 3;
const RECENT_AUTO_SEARCH_WINDOW_MS = 1500;
const recentAutoSearches = new Map();

function formatMoney(amount, currency, locale) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
}

function parsePositiveNumber(value, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  return num;
}

function guestFallbackKey() {
  const dayKey = new Date().toISOString().slice(0, 10);
  return `budget_recipe_guest_usage:${dayKey}`;
}

function readGuestFallbackUsage() {
  try {
    return Number(localStorage.getItem(guestFallbackKey()) || 0) || 0;
  } catch {
    return 0;
  }
}

function writeGuestFallbackUsage(count) {
  try {
    localStorage.setItem(guestFallbackKey(), String(count));
  } catch {}
}

function shouldSkipRecentAutoSearch(key) {
  const now = Date.now();
  const previous = recentAutoSearches.get(key);
  if (previous && now - previous < RECENT_AUTO_SEARCH_WINDOW_MS) {
    return true;
  }
  recentAutoSearches.set(key, now);
  return false;
}

function BudgetRecipeSkeleton() {
  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? `0 18px 44px ${alpha("#000000", 0.28)}`
            : `0 18px 44px ${alpha(theme.palette.primary.main, 0.08)}`,
      }}
    >
      <Skeleton variant="rectangular" height={210} />
      <CardContent>
        <Skeleton variant="text" height={36} width="72%" />
        <Skeleton variant="text" height={24} width="48%" />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 2 }}>
          <Skeleton variant="rounded" width={88} height={28} />
          <Skeleton variant="rounded" width={104} height={28} />
        </Stack>
        <Skeleton variant="rounded" height={92} />
      </CardContent>
    </Card>
  );
}

export default function BudgetRecipesPage() {
  const { country } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const { country: ctxCountry, usZip, usState, storeLocationId } = useLocationCtx();
  const resolvedCountry = resolveCountryId({ locationCountry: ctxCountry });
  const locale = String(i18n.language || country || "en");
  const isPremium = userHasActiveEntitlement(user);
  const isDark = theme.palette.mode === "dark";

  const [people, setPeople] = useState(() => parsePositiveNumber(searchParams.get("people"), DEFAULT_PEOPLE));
  const [budget, setBudget] = useState(() => parsePositiveNumber(searchParams.get("budget"), DEFAULT_BUDGET));
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const requestSeqRef = useRef(0);
  const latestAppliedRequestRef = useRef(0);

  const debugBudgetRecipes = (...args) => {
    try {
      const enabled =
        window.localStorage?.getItem("debug_budget_recipes") === "1" ||
        process.env.REACT_APP_DEBUG_BUDGET_RECIPES === "1";
      if (enabled) {
        // eslint-disable-next-line no-console
        console.log("[BudgetRecipesPage]", ...args);
      }
    } catch {}
  };

  const pathWithoutLocale =
    (location.pathname || "/").replace(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i, "") || "/";

  const runSearch = async (nextPeople, nextBudget, { source = "manual" } = {}) => {
    const safePeople = parsePositiveNumber(nextPeople, DEFAULT_PEOPLE);
    const safeBudget = parsePositiveNumber(nextBudget, DEFAULT_BUDGET);
    const searchKey = JSON.stringify({
      source,
      people: safePeople,
      budget: safeBudget,
      locale,
      countryId: resolvedCountry.id || "1",
      usZip: resolvedCountry.id === "2" ? (usZip || "") : "",
      usState: resolvedCountry.id === "2" ? (usState || "") : "",
      storeLocationId: resolvedCountry.id === "2" ? (storeLocationId || "") : "",
      isAuthenticated: Boolean(user),
    });

    if (source === "auto" && shouldSkipRecentAutoSearch(searchKey)) {
      debugBudgetRecipes("skip duplicate auto search", { searchKey });
      return;
    }

    const requestId = ++requestSeqRef.current;

    setHasSearched(true);
    setLoading(true);
    setError("");
    debugBudgetRecipes("loading -> true", { requestId, source, searchKey });
    setSearchParams({
      people: String(safePeople),
      budget: String(safeBudget),
    });

    try {
      const payload = {
        people: safePeople,
        budget: safeBudget,
        lang: locale,
        country_id: resolvedCountry.id || "1",
        zip_code: resolvedCountry.id === "2" ? (usZip || "") : "",
        us_state: resolvedCountry.id === "2" ? (usState || "") : "",
        store_location_id: resolvedCountry.id === "2" ? (storeLocationId || "") : "",
      };
      debugBudgetRecipes("budgetSearch request", { requestId, source, payload });
      const res = await api.recipes.budgetSearch(payload);
      let nextResults = res?.data?.results || [];
      debugBudgetRecipes("budgetSearch raw response", {
        requestId,
        source,
        success: res?.data?.success,
        resultCount: Array.isArray(res?.data?.results) ? res.data.results.length : null,
        usage: res?.data?.usage || null,
        firstRecipe: res?.data?.results?.[0]
          ? {
              id: res.data.results[0].id,
              name: res.data.results[0].name,
              cost_per_person: res.data.results[0].cost_per_person,
              recipe_total_cost: res.data.results[0].recipe_total_cost,
              shopping_total_cost: res.data.results[0].shopping_total_cost,
            }
          : null,
      });
      setMeta(res?.data?.meta || null);
      setCurrency(res?.data?.currency || (resolvedCountry.id === "2" ? "USD" : "EUR"));
      let nextUsage = res?.data?.usage || null;

      debugBudgetRecipes("results before guest/auth logic", {
        requestId,
        source,
        resultCount: nextResults.length,
        usage: nextUsage,
      });

      if (!user && nextUsage?.storage_scope === "untracked") {
        const usedToday = readGuestFallbackUsage() + 1;
        writeGuestFallbackUsage(usedToday);
        nextUsage = {
          ...nextUsage,
          used_today: usedToday,
          remaining_today: Math.max(GUEST_FALLBACK_LIMIT - usedToday, 0),
          limit_reached: usedToday > GUEST_FALLBACK_LIMIT,
          daily_limit: GUEST_FALLBACK_LIMIT,
        };
        debugBudgetRecipes("guest fallback usage applied", {
          requestId,
          source,
          usedToday,
          nextUsage,
          resultCount: nextResults.length,
        });
      }

      debugBudgetRecipes("results before setState", {
        requestId,
        source,
        resultCount: nextResults.length,
        usage: nextUsage,
      });
      if (requestId < latestAppliedRequestRef.current) {
        debugBudgetRecipes("skip stale response", {
          requestId,
          latestAppliedRequest: latestAppliedRequestRef.current,
        });
        return;
      }
      latestAppliedRequestRef.current = requestId;
      setResults(nextResults);
      setUsage(nextUsage);
      debugBudgetRecipes("state applied", {
        requestId,
        appliedResultCount: nextResults.length,
        usage: nextUsage,
      });
    } catch (err) {
      debugBudgetRecipes("search failed, clearing results", {
        requestId,
        source,
        message: err?.message || String(err),
      });
      setResults([]);
      setMeta(null);
      setCurrency(resolvedCountry.id === "2" ? "USD" : "EUR");
      setUsage(null);
      setError(
        t(
          "budget_recipes.error",
          "We couldn't load recipe suggestions right now. Please try again."
        )
      );
    } finally {
      setLoading(false);
      debugBudgetRecipes("loading -> false", { requestId, source });
    }
  };

  useEffect(() => {
    const qsPeople = searchParams.get("people");
    const qsBudget = searchParams.get("budget");
    if (!qsPeople || !qsBudget) return;
    debugBudgetRecipes("auto-search effect fired", {
      qsPeople,
      qsBudget,
      locale,
      userId: user?.id || null,
    });
    runSearch(
      parsePositiveNumber(qsPeople, DEFAULT_PEOPLE),
      parsePositiveNumber(qsBudget, DEFAULT_BUDGET),
      { source: "auto" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(people, budget, { source: "manual" });
  };

  const withinBudgetCount = meta?.within_budget_count || 0;
  const perPersonBudget = budget / Math.max(Number(people) || 1, 1);
  const usageTier = usage?.tier || (isPremium ? "premium" : user ? "free" : "guest");
  const isLimitReached = Boolean(usage?.limit_reached);
  const isGuestLimitReached = usageTier === "guest" && isLimitReached;
  const remainingToday = usage?.remaining_today;
  const dailyLimit = usage?.daily_limit;
  const pageBackground = isDark
    ? `radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(
        theme.palette.background.paper,
        0.94
      )} 42%, ${theme.palette.background.default} 100%)`
    : `radial-gradient(circle at top left, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(
        theme.palette.background.paper,
        0.97
      )} 46%, ${theme.palette.background.default} 100%)`;
  const surfaceGradient = isDark
    ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(
        theme.palette.background.default,
        0.94
      )})`
    : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(
        theme.palette.primary.light,
        0.08
      )})`;
  const highlightedSurface = isDark
    ? alpha(theme.palette.primary.main, 0.1)
    : alpha(theme.palette.primary.main, 0.05);
  const subtleChipBackground = isDark
    ? alpha(theme.palette.background.paper, 0.82)
    : alpha(theme.palette.background.paper, 0.92);
  const elevatedShadow = isDark
    ? `0 20px 56px ${alpha("#000000", 0.34)}`
    : `0 24px 70px ${alpha(theme.palette.primary.main, 0.12)}`;
  const cardShadow = isDark
    ? `0 18px 44px ${alpha("#000000", 0.32)}`
    : `0 18px 44px ${alpha(theme.palette.primary.main, 0.08)}`;
  const cardHoverShadow = isDark
    ? `0 24px 60px ${alpha("#000000", 0.42)}`
    : `0 24px 60px ${alpha(theme.palette.primary.main, 0.14)}`;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 6 },
        background: pageBackground,
        color: "text.primary",
      }}
    >
      <Helmet>
        <title>{t("budget_recipes.meta_title", "Budget Recipes | InCooking")}</title>
        <meta
          name="description"
          content={t(
            "budget_recipes.meta_description",
            "Find recipe ideas that fit your budget and the number of people you want to cook for."
          )}
        />
        <link rel="canonical" href={`${FRONTEND_URL}${location.pathname || "/"}`} />
        {getPathHreflangLinks(pathWithoutLocale, "en")}
      </Helmet>

      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Stack spacing={2.25}>
              <Chip
                icon={<AutoAwesomeIcon />}
                label={t("budget_recipes.eyebrow", "Premium budget recipe finder")}
                sx={{
                  alignSelf: "flex-start",
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 1,
                  bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12)}`,
                }}
              />

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.3rem", sm: "3rem", md: "3.6rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  fontWeight: 900,
                  maxWidth: 760,
                }}
              >
                {t(
                  "budget_recipes.hero_title",
                  "Find recipe ideas for your people and your budget"
                )}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  maxWidth: 650,
                  color: "text.secondary",
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "budget_recipes.hero_subtitle",
                  "Tell us how many people you want to cook for and how much you want to spend. We'll rank public recipes using InCooking's real ingredient pricing logic."
                )}
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Chip
                  icon={<Groups2Icon />}
                  label={t("budget_recipes.hero_people_chip", {
                    defaultValue: "{{count}} people",
                    count: Number(people) || DEFAULT_PEOPLE,
                  })}
                  sx={{
                    fontWeight: 700,
                    bgcolor: subtleChipBackground,
                    color: "text.primary",
                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.65 : 0.9)}`,
                  }}
                />
                <Chip
                  icon={<PaidOutlinedIcon />}
                  label={t("budget_recipes.hero_budget_chip", {
                    defaultValue: "{{budget}} total budget",
                    budget: formatMoney(budget, currency, locale),
                  })}
                  sx={{
                    fontWeight: 700,
                    bgcolor: subtleChipBackground,
                    color: "text.primary",
                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.65 : 0.9)}`,
                  }}
                />
                <Chip
                  icon={<TuneIcon />}
                  label={t("budget_recipes.hero_value_chip", {
                    defaultValue: "{{budget}} per person target",
                    budget: formatMoney(perPersonBudget, currency, locale),
                  })}
                  sx={{
                    fontWeight: 700,
                    bgcolor: subtleChipBackground,
                    color: "text.primary",
                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.65 : 0.9)}`,
                  }}
                />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: { xs: 2.25, sm: 3.25 },
                height: "100%",
                borderRadius: 5,
                border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.7 : 1)}`,
                boxShadow: elevatedShadow,
                background: surfaceGradient,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
                  {t("budget_recipes.form_title", "Set your cooking target")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t(
                    "budget_recipes.form_subtitle",
                    "A smart shortlist for blog readers, new visitors, and anyone trying to cook well on a clear budget."
                  )}
                </Typography>

                <TextField
                  type="number"
                  label={t("budget_recipes.people_label", "Number of people")}
                  value={people}
                  onChange={(e) => setPeople(e.target.value)}
                  inputProps={{ min: 1, max: 24, step: 1 }}
                  fullWidth
                />

                <TextField
                  type="number"
                  label={t("budget_recipes.budget_label", "Available budget")}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  inputProps={{ min: 1, max: 500, step: 0.5 }}
                  fullWidth
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: highlightedSurface,
                    borderColor: alpha(theme.palette.divider, isDark ? 0.7 : 1),
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t("budget_recipes.helper_title", "What we optimize for")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {t(
                      "budget_recipes.helper_body",
                      "Recipes within budget rank first, then we show the options closest to your target without hiding near matches."
                    )}
                  </Typography>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.background.paper, isDark ? 0.72 : 0.82),
                    borderColor: alpha(theme.palette.divider, isDark ? 0.7 : 1),
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {usageTier === "premium"
                      ? t("budget_recipes.usage.premium_title", "Premium: unlimited searches")
                      : usageTier === "free"
                        ? t("budget_recipes.usage.free_title", "Free plan: 10 searches per day")
                        : t("budget_recipes.usage.guest_title", "Guest access: 3 searches per day")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {usageTier === "premium"
                      ? t("budget_recipes.usage.premium_body", "You can explore the full feature without daily limits.")
                      : usageTier === "free"
                        ? t("budget_recipes.usage.free_body", "You get full results with a daily search cap. Upgrade to Premium for unlimited use.")
                        : t("budget_recipes.usage.guest_body", "Guests can try the feature with the top 3 results per search. Create an account to unlock more daily searches.")}
                  </Typography>
                  {dailyLimit != null ? (
                    <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 700 }}>
                      {t("budget_recipes.usage.remaining", {
                        defaultValue: "{{count}} searches remaining today",
                        count: Math.max(Number(remainingToday ?? dailyLimit), 0),
                      })}
                    </Typography>
                  ) : null}
                </Paper>

                {isLimitReached ? (
                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                      {usageTier === "guest"
                        ? t("budget_recipes.limit.guest_title", "Guest limit reached")
                        : t("budget_recipes.limit.free_title", "Daily limit reached")}
                    </Typography>
                    <Typography variant="body2">
                      {usageTier === "guest"
                        ? t("budget_recipes.limit.guest_body", "Create an account to keep using budget recipe matching today, or upgrade to Premium for unlimited access.")
                        : t("budget_recipes.limit.free_body", "Upgrade to Premium to keep searching beyond your daily free limit.")}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.25 }}>
                      {!user ? (
                        <>
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/${country || locale}/register`)}
                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                          >
                            {t("budget_recipes.limit.create_account_cta", "Create account")}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => navigate(`/${country || locale}/plans`)}
                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                          >
                            {t("budget_recipes.limit.upgrade_cta", "Upgrade to Premium")}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/${country || locale}/plans`)}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                        >
                          {t("budget_recipes.limit.upgrade_cta", "Upgrade to Premium")}
                        </Button>
                      )}
                    </Stack>
                  </Alert>
                ) : null}

                <Button
                  type="submit"
                  size="large"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.4,
                    borderRadius: 999,
                    fontWeight: 800,
                    fontSize: "1rem",
                    textTransform: "none",
                    boxShadow: `0 16px 38px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.22)}`,
                  }}
                >
                  {loading
                    ? t("budget_recipes.loading_cta", "Finding recipe ideas...")
                    : t("budget_recipes.cta", "Find recipes")}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.03em" }}>
                {t("budget_recipes.results_title", "Recommended recipes")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
                {!hasSearched
                  ? t("budget_recipes.results_idle", "Set your people and budget to see ranked recipe ideas.")
                  : loading
                  ? t("budget_recipes.results_loading", "Calculating recipe matches for your target...")
                  : t("budget_recipes.results_summary", {
                    defaultValue: "{{count}} recipes within budget for {{people}} people",
                    count: withinBudgetCount,
                    people: Number(people) || DEFAULT_PEOPLE,
                  })}
              </Typography>
            </Box>

            {!loading && results.length > 0 ? (
              <Chip
                label={t("budget_recipes.summary_chip", {
                  defaultValue: "{{count}} ranked options",
                  count: results.length,
                })}
                sx={{
                  fontWeight: 800,
                  bgcolor: subtleChipBackground,
                  color: "text.primary",
                  border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.65 : 0.9)}`,
                }}
              />
            ) : null}
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }}>
              {error}
            </Alert>
          ) : null}

          {!loading && hasSearched && usageTier === "guest" && results.length > 0 && !isLimitReached ? (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 3 }}>
              {t("budget_recipes.guest_result_hint", "Guests see the top 3 matches per search.")}
            </Alert>
          ) : null}

          {!hasSearched ? (
            <Paper
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 5,
                border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.7 : 1)}`,
                background: surfaceGradient,
                boxShadow: isDark
                  ? `0 14px 36px ${alpha("#000000", 0.24)}`
                  : `0 14px 36px ${alpha(theme.palette.primary.main, 0.06)}`,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                {t("budget_recipes.idle_title", "Start with your budget target")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 640 }}>
                {t("budget_recipes.idle_body", "Choose how many people you want to cook for and your available budget, and we'll rank the best public recipes for that target.")}
              </Typography>
            </Paper>
          ) : loading ? (
            <Grid container spacing={2.5}>
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <Grid item xs={12} sm={6} lg={4} key={item}>
                  <BudgetRecipeSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : isGuestLimitReached ? (
            <Paper
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 5,
                border: `1px solid ${alpha(theme.palette.warning.main, isDark ? 0.45 : 0.28)}`,
                background: isDark
                  ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(
                      theme.palette.warning.dark,
                      0.12
                    )})`
                  : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(
                      theme.palette.warning.light,
                      0.18
                    )})`,
                boxShadow: isDark
                  ? `0 14px 36px ${alpha("#000000", 0.24)}`
                  : `0 14px 36px ${alpha(theme.palette.warning.main, 0.08)}`,
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {t("budget_recipes.limit.guest_title", "Guest limit reached")}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 640 }}>
                  {t(
                    "budget_recipes.limit.guest_panel_body",
                    "You have reached today’s guest search limit. Log in or create an account to continue."
                  )}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/${country || locale}/login`)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                  >
                    {t("login", "Login")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/${country || locale}/register`)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                  >
                    {t("budget_recipes.limit.create_account_cta", "Create account")}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : results.length === 0 ? (
            <Paper
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 5,
                border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.7 : 1)}`,
                background: surfaceGradient,
                boxShadow: isDark
                  ? `0 14px 36px ${alpha("#000000", 0.24)}`
                  : `0 14px 36px ${alpha(theme.palette.primary.main, 0.06)}`,
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {t("budget_recipes.empty_title", "No recipe matches yet")}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 620 }}>
                  {t(
                    "budget_recipes.empty_body",
                    "Try a slightly higher budget or fewer people and we'll look again using the same recipe pricing logic."
                  )}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const nextBudget = Math.max(Number(budget) || DEFAULT_BUDGET, DEFAULT_BUDGET) + 5;
                      setBudget(nextBudget);
                      runSearch(people, nextBudget);
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                  >
                    {t("budget_recipes.empty_budget_cta", "Try with +5 budget")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const nextPeople = Math.max(1, (Number(people) || DEFAULT_PEOPLE) - 1);
                      setPeople(nextPeople);
                      runSearch(nextPeople, budget);
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                  >
                    {t("budget_recipes.empty_people_cta", "Try fewer people")}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              {results.map((recipe) => {
                const image = resolveRecipeImage(recipe?.image_url);
                const badges = Array.isArray(recipe?.badges) ? recipe.badges : [];
                const recipePath = buildRecipePath(recipe, country || locale);

                return (
                  <Grid item xs={12} sm={6} lg={4} key={recipe.id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 4,
                        overflow: "hidden",
                        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.7 : 1)}`,
                        boxShadow: cardShadow,
                        background: surfaceGradient,
                        transition: "transform 180ms ease, box-shadow 180ms ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: cardHoverShadow,
                        },
                      }}
                    >
                      <CardActionArea onClick={() => navigate(recipePath)} sx={{ height: "100%", alignItems: "stretch" }}>
                        <CardMedia
                          component="img"
                          height="220"
                          image={image}
                          alt={recipe.name || "Recipe"}
                        />
                        <CardContent sx={{ p: 2.25 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                            {badges.includes("within_budget") ? (
                              <Chip
                                size="small"
                                color="success"
                                label={t("budget_recipes.badges.within_budget", "Within budget")}
                                sx={{ fontWeight: 800 }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("budget_recipes.badges.over_budget", "Near your budget")}
                                sx={{ fontWeight: 800 }}
                              />
                            )}
                            {badges.includes("best_value") ? (
                              <Chip
                                size="small"
                                label={t("budget_recipes.badges.best_value", "Best value")}
                                sx={{
                                  fontWeight: 800,
                                  bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12),
                                  color: theme.palette.primary.main,
                                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.14)}`,
                                }}
                              />
                            ) : null}
                            {badges.includes("budget_pick") ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t("budget_recipes.badges.budget_pick", "Budget pick")}
                                sx={{ fontWeight: 800 }}
                              />
                            ) : null}
                          </Stack>

                          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15, minHeight: 58 }}>
                            {recipe.name}
                          </Typography>

                          <Stack direction="row" spacing={1.5} sx={{ mt: 1.25, color: "text.secondary", flexWrap: "wrap" }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Groups2Icon sx={{ fontSize: 18 }} />
                              <Typography variant="body2">
                                {t("budget_recipes.card_people", {
                                  defaultValue: "{{count}} people",
                                  count: recipe.requested_people,
                                })}
                              </Typography>
                            </Stack>
                            {recipe.prep_time ? (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <AccessTimeIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">
                                  {t("budget_recipes.card_prep_time", {
                                    defaultValue: "{{count}} min",
                                    count: recipe.prep_time,
                                  })}
                                </Typography>
                              </Stack>
                            ) : null}
                          </Stack>

                          <Paper
                            variant="outlined"
                            sx={{
                              mt: 2,
                              p: 1.5,
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.background.default, isDark ? 0.72 : 0.7),
                              borderColor: alpha(theme.palette.divider, isDark ? 0.7 : 1),
                            }}
                          >
                            <Grid container spacing={1.25}>
                              <Grid item xs={7}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {t("budget_recipes.card_recipe_cost", {
                                    defaultValue: "Recipe cost (for {{count}} people)",
                                    count: recipe.requested_people,
                                  })}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                  {formatMoney(
                                    recipe.recipe_total_cost ?? recipe.estimated_recipe_cost,
                                    currency,
                                    locale
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {t("budget_recipes.card_cost_per_person", "Cost / person")}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                  {formatMoney(recipe.cost_per_person, currency, locale)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  {t("budget_recipes.card_total_cost", {
                                    defaultValue: "Shopping cost: {{value}}",
                                    value: formatMoney(
                                      recipe.shopping_total_cost ?? recipe.shopping_cost ?? recipe.estimated_total_cost,
                                      currency,
                                      locale
                                    ),
                                  })}
                                </Typography>
                                {isPremium &&
                                recipe.inventory_pricing_available &&
                                Number(recipe.inventory_covered_ingredients_count || 0) > 0 ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "success.main", fontWeight: 700, mt: 0.25 }}
                                  >
                                    {t("budget_recipes.card_out_of_pocket_cost", {
                                      defaultValue: "With your inventory: {{value}}",
                                      value: formatMoney(
                                        recipe.out_of_pocket_cost ?? recipe.inventory_adjusted_cost,
                                        currency,
                                        locale
                                      ),
                                    })}
                                  </Typography>
                                ) : null}
                                {(recipe.package_cost ?? recipe.estimated_basket_cost) > 0 ? (
                                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                                    {t("budget_recipes.card_packages_cost", {
                                      defaultValue: "Package estimate: {{value}}",
                                      value: formatMoney(
                                        recipe.package_cost ?? recipe.estimated_basket_cost,
                                        currency,
                                        locale
                                      ),
                                    })}
                                  </Typography>
                                ) : null}
                                {recipe.missing_count > 0 ? (
                                  <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 700, mt: 0.5 }}>
                                    {t("budget_recipes.card_missing_prices", {
                                      defaultValue: "Missing price data for {{count}} ingredients",
                                      count: recipe.missing_count,
                                    })}
                                  </Typography>
                                ) : null}
                              </Grid>
                            </Grid>
                          </Paper>

                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: 2.2 }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <RestaurantMenuIcon sx={{ color: "primary.main", fontSize: 20 }} />
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {recipe.meal_type_name || recipe.food_type_name || t("budget_recipes.card_cta_hint", "Open recipe details")}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 800, color: "primary.main", display: "flex", alignItems: "center", gap: 0.75 }}
                            >
                              {t("budget_recipes.card_cta", "See recipe")}
                              <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            </Typography>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Container>
    </Box>
  );
}
